/**
 * Picks the right Auth0 provider at boot:
 *  - preview session active -> `MockAuth0Provider` (no real Auth0 required)
 *  - otherwise -> real `<Auth0Provider>`
 *
 * The decision is read once at mount. Switching modes requires a full reload,
 * which `previewMode.ts` triggers via `exitPreviewSession()` /
 * `enterPreviewSession()`. This keeps Auth0's internal state machine sane —
 * swapping providers mid-render would tear down the React tree.
 *
 * Why this exists: `PREVIEW_MODE`/`VITE_PREVIEW_MODE` used to be a build-time
 * server-wide toggle, which entangled human dev sessions with any AI agent
 * sharing the dev server. Per-client opt-out via cookie + localStorage
 * decouples the two — see `previewMode.ts` for the contract.
 */
import type { ReactNode } from 'react';
import { Auth0Provider, type Auth0ProviderOptions } from '@auth0/auth0-react';

import { MockAuth0Provider } from './MockAuth0Provider';
import { isPreviewSessionActive } from './previewMode';

export type DevAuthProviderProps = Pick<
  Auth0ProviderOptions,
  | 'domain'
  | 'clientId'
  | 'authorizationParams'
  | 'useRefreshTokens'
  | 'cacheLocation'
  | 'onRedirectCallback'
> & {
  children: ReactNode;
};

export const DevAuthProvider = ({
  children,
  ...auth0ProviderProps
}: DevAuthProviderProps) => {
  if (isPreviewSessionActive()) {
    return <MockAuth0Provider>{children}</MockAuth0Provider>;
  }
  return <Auth0Provider {...auth0ProviderProps}>{children}</Auth0Provider>;
};
